import React, { useState, useEffect } from 'react';
import {
  blockTypeSupportsInteractionSettings,
  blockTypeSupportsSuccessAnimationSettings,
  blockTypeSupportsHideResults,
  blockTypeForcesHideResults,
  designTokens,
  getBlockDefaultInteractionMessage,
  useAccountConfig,
} from '@course-authoring/shared-types';
import type {
  CourseBlock,
  SuccessAnimationType,
} from '@course-authoring/shared-types';

type SuccessAnimationSelection = 'none' | SuccessAnimationType;

const SUCCESS_ANIMATION_OPTIONS: Array<{
  value: SuccessAnimationSelection;
  label: string;
}> = [
    { value: 'none', label: 'None' },
    { value: 'confetti', label: 'Confetti' },
    { value: 'pixie-dust', label: 'Pixie Dust' },
    { value: 'gold-coins', label: 'Gold Coins' },
  ];

export interface BlockSettingsUpdates {
  requireInteraction?: boolean;
  interactionMessage?: string;
  successAnimationType?: SuccessAnimationType;
  hideResults?: boolean;
}

interface BlockSettingsModalProps {
  block: CourseBlock;
  onClose: () => void;
  onSave: (updates: BlockSettingsUpdates) => void;
  translationMode?: boolean;
  interactionMessageValue?: string;
  readOnly?: boolean;
}

export function BlockSettingsModal({
  block,
  onClose,
  onSave,
  translationMode = false,
  interactionMessageValue,
  readOnly = false,
}: BlockSettingsModalProps) {
  const supportsInteractionSettings = blockTypeSupportsInteractionSettings(block.type);
  const supportsInteractionMessage = supportsInteractionSettings && block.type !== 'map';
  const supportsSuccessAnimation = blockTypeSupportsSuccessAnimationSettings(block.type);
  const isGatedSingleChoice = block.type === 'single-choice-answer' && block.gated === true;
  const supportsHideResults =
    blockTypeSupportsHideResults(block.type) && !isGatedSingleChoice;
  // NA-444: Freeform Text has no results view at all - the checkbox still
  // renders (so Block Settings isn't empty), but checked and disabled,
  // mirroring the isGatedSingleChoice special-case's shape above.
  const forcesHideResults = blockTypeForcesHideResults(block.type);
  const defaultInteractionMessage = getBlockDefaultInteractionMessage(block.type);
  const [requireInteraction, setRequireInteraction] = useState(
    block.requireInteraction ?? false
  );
  const [interactionMessage, setInteractionMessage] = useState(
    translationMode ? interactionMessageValue ?? '' : block.interactionMessage ?? ''
  );
  const [successAnimationType, setSuccessAnimationType] = useState<SuccessAnimationSelection>(
    block.successAnimationType ?? 'none'
  );
  const [hideResults, setHideResults] = useState(forcesHideResults ? true : (block.hideResults ?? false));
  const { hideResultsFromLearners } = useAccountConfig();

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Set default message when toggle is turned on if empty
  useEffect(() => {
    if (translationMode || !supportsInteractionMessage) {
      return;
    }

    if (requireInteraction && !interactionMessage && defaultInteractionMessage) {
      setInteractionMessage(defaultInteractionMessage);
    }
  }, [defaultInteractionMessage, interactionMessage, requireInteraction, supportsInteractionMessage, translationMode]);

  const handleSave = () => {
    if (readOnly) return;

    if (translationMode) {
      onSave({ interactionMessage });
      onClose();
      return;
    }

    const updates: BlockSettingsUpdates = {};

    if (supportsInteractionSettings) {
      updates.requireInteraction = requireInteraction;
    }
    if (
      supportsInteractionMessage
      && interactionMessage !== (block.interactionMessage ?? '')
    ) {
      updates.interactionMessage = interactionMessage;
    }

    if (supportsSuccessAnimation) {
      updates.successAnimationType =
        successAnimationType === 'none' ? undefined : successAnimationType;
    }

    if (supportsHideResults) {
      updates.hideResults = hideResults;
    }
    if (forcesHideResults) {
      updates.hideResults = true;
    }

    onSave(updates);
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Block Settings</h3>
          <button onClick={onClose} style={styles.closeButton}>
            ✕
          </button>
        </div>

        <div style={styles.content}>
          {!translationMode && supportsInteractionSettings && (
            <div style={styles.field}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={requireInteraction}
                  onChange={(e) => setRequireInteraction(e.target.checked)}
                  style={styles.checkbox}
                />
                <span style={styles.labelText}>Require interaction to pass</span>
              </label>
              <p style={styles.helpText}>
                Learners must complete the interaction for this block before they can proceed to the next block.
              </p>
            </div>
          )}

          {supportsInteractionMessage && (translationMode || requireInteraction) && (
            <div style={styles.field}>
              <label style={styles.label}>
                <span style={styles.labelText}>Informational Message</span>
                <input
                  type="text"
                  value={interactionMessage}
                  onChange={(e) => setInteractionMessage(e.target.value)}
                  disabled={readOnly}
                  style={styles.input}
                  placeholder={
                    defaultInteractionMessage
                      ? `e.g. ${defaultInteractionMessage}`
                      : 'Enter an informational message'
                  }
                />
              </label>
              <p style={styles.helpText}>
                This message will be shown to learners below the block.
              </p>
            </div>
          )}

          {!translationMode && supportsSuccessAnimation && (
            <div style={styles.field}>
              <label style={styles.label}>
                <span style={styles.labelText}>Success animation</span>
                <select
                  aria-label="Success animation"
                  value={successAnimationType}
                  onChange={(e) =>
                    setSuccessAnimationType(e.target.value as SuccessAnimationSelection)
                  }
                  style={styles.select}
                >
                  {SUCCESS_ANIMATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <p style={styles.helpText}>
                Choose the celebration that plays when learners scroll to the end of this content block.
              </p>
            </div>
          )}

          {!translationMode && (supportsHideResults || forcesHideResults) && (
            <div style={styles.field}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={hideResults || hideResultsFromLearners}
                  disabled={forcesHideResults || hideResultsFromLearners}
                  onChange={(e) => setHideResults(e.target.checked)}
                  style={styles.checkbox}
                />
                <span style={styles.labelText}>Hide results after submission</span>
              </label>
              <p style={styles.helpText}>
                Instead of showing results, display a &ldquo;Thank you for your response.&rdquo; message after the learner submits.
              </p>
              {supportsHideResults && hideResultsFromLearners && (
                <p style={styles.helpText}>Results hidden from learner at the account level.</p>
              )}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelButton}>
            Cancel
          </button>
          <button onClick={handleSave} style={styles.saveButton} disabled={readOnly}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    width: '400px',
    maxWidth: '90vw',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${designTokens.colors.textMuted}`,
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: designTokens.colors.textPrimary,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    color: designTokens.colors.textSecondary,
    padding: '4px',
  },
  content: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  labelText: {
    fontSize: '14px',
    fontWeight: 500,
    color: designTokens.colors.textPrimary,
  },
  helpText: {
    margin: 0,
    fontSize: '12px',
    color: designTokens.colors.textSecondary,
    lineHeight: '1.4',
  },
  input: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: `1px solid ${designTokens.colors.textMuted}`,
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: `1px solid ${designTokens.colors.textMuted}`,
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: designTokens.colors.textPrimary,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: `1px solid ${designTokens.colors.textMuted}`,
    backgroundColor: designTokens.colors.inspectorBackground,
    borderRadius: '0 0 8px 8px',
  },
  cancelButton: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: `1px solid ${designTokens.colors.textMuted}`,
    background: '#fff',
    color: designTokens.colors.textPrimary,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  saveButton: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    background: designTokens.colors.selectionBorder,
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
